import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { InferType } from 'yup';

import { Event } from '../../types';

import {
  APNotification,
  getConflictingEvents,
  notificationsToHashTable
} from '../../utils';

import { UpdateRouteStartPayload } from 'RestAPIs/activities';

import { dialogManagerActiveAtom } from '../../atoms';

import { LoadingButton } from '@mui/lab';
import { Button, DialogActions, Grid, Typography } from '@mui/material';

import {
  ControlledDatePicker,
  ControlledTimePicker,
  ControlledToggleButtonGroup,
  Dialog,
  showSnackbar
} from 'components/shared';

import { useUpdateRouteStart } from '../../mutations';
import { DialogType } from '../DialogManager/DialogManager';
import editRouteSchema from './edit-route-schema';

const schema = editRouteSchema();
type Form = InferType<typeof schema>;

type Props = {
  open: boolean;
  handleClose: () => void;
  appointmentGroupingEntityId: string;
  appointmentGroupingStart: string;
  events: Event[];
};

export default function EditRouteDialog({
  open,
  handleClose,
  appointmentGroupingEntityId,
  appointmentGroupingStart,
  events
}: Props) {
  const [, setDialogState] = useRecoilState(dialogManagerActiveAtom);
  const { t } = useTranslation('common');
  const firstEvent: Event = events.filter(
    ({ category }) => category === 'NON_CLIENT' || category === 'CLIENT'
  )[0];
  const { start: appointmentStart, isRecurring } = firstEvent;

  const defaultValues = {
    startTime: dayjs(appointmentStart),
    startDate: dayjs(appointmentStart),
    editAppointmentType: undefined
  };

  const { control, handleSubmit, reset, watch } = useForm<Form>({
    resolver: yupResolver(schema),
    defaultValues
  });

  useEffect(() => {
    reset(defaultValues);
  }, [appointmentStart]);

  const { mutate, isLoading } = useUpdateRouteStart();

  function onSubmit(data: any) {
    const { startDate, startTime, editAppointmentType } = data;

    const start = dayjs(startDate)
      .set('hour', startTime.hour())
      .set('minute', startTime.minute())
      .toISOString();

    const payload: UpdateRouteStartPayload = {
      appointmentGroupingStart: appointmentGroupingStart,
      appointmentGroupingId: appointmentGroupingEntityId,
      editAppointmentType: editAppointmentType,
      suppressWarnings: false,
      start
    };

    mutate(payload, {
      onSuccess: (response: any) => {
        if (!response.data.success) {
          const notifications = response.data.notifications as APNotification[];
          const conflictingEvents: Event[] = getConflictingEvents(
            events || {},
            notifications
          );
          handleClose();
          setDialogState({
            type: DialogType.WARNING,
            open: true,
            data: notificationsToHashTable(conflictingEvents, notifications),
            onCancel: () => {
              reset(defaultValues);
            },
            onSubmit: () => {
              mutate(
                {
                  ...payload,
                  suppressWarnings: true
                } as UpdateRouteStartPayload,
                {
                  onSuccess: () => {
                    showSnackbar({
                      severity: 'success',
                      message: t('changesSaved')
                    });
                  }
                }
              );
            }
          });
        } else {
          showSnackbar({
            severity: 'success',
            message: t('changesSaved')
          });
          handleClose();
        }
      }
    });
  }

  return (
    <Dialog
      title={t('activityPlanning.routeContextMenu.editRouteStart')}
      open={open}
      handleClose={handleClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h4" mb={2}>
          {t('activityPlanning.routeContextMenu.infoRouteStart')}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <ControlledDatePicker
              name="startDate"
              label={t('date')}
              control={control}
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledTimePicker
              name="startTime"
              label={t('startTime')}
              control={control}
            />
          </Grid>
        </Grid>
        {isRecurring && (
          <Grid item xs={6} mt={3}>
            <ControlledToggleButtonGroup
              control={control}
              name="editAppointmentType"
              color="secondary"
              aria-label="alignment"
              inputToggleButtons={[
                {
                  label: t('activityPlanning.onlyThisAppointment'),
                  value: 'ONLY_THIS',
                  selected: watch<any>('editAppointmentType') === 'ONLY_THIS'
                },
                {
                  label: t('activityPlanning.thisAndSubsequentAppointments'),
                  value: 'THIS_AND_ALL_SUBSEQUENT',
                  selected:
                    watch<any>('editAppointmentType') ===
                    'THIS_AND_ALL_SUBSEQUENT'
                }
              ]}
            />
          </Grid>
        )}
        <DialogActions sx={{ mt: 3 }}>
          <Button
            type="button"
            variant="text"
            color="dark"
            onClick={() => {
              handleClose();
            }}
          >
            {t('cancel')}
          </Button>
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            color="dark"
            disabled={
              isRecurring
                ? watch<any>('editAppointmentType') === undefined
                : false
            }
          >
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
